/**
 * Tipografia do widget Cabeçalho de seção (section-header): nível de título,
 * tamanhos locais ou alinhados à tipografia global do curso.
 */

export const SECTION_HEADER_TITLE_CLASS = 'madri-gc-section-header__heading';

const LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

function parsePositiveInt(v, fallback = null) {
  const n = parseInt(v, 10);
  if (Number.isFinite(n) && n > 0) return n;
  return fallback;
}

/**
 * @param {string} tag h1–h6
 * @param {Record<string, unknown>|null|undefined} globalConfig
 * @returns {number}
 */
export function resolveGlobalHeadingPx(tag, globalConfig) {
  const g = globalConfig || {};
  const h1 = parsePositiveInt(g.h1Size, 36);
  const h2 = parsePositiveInt(g.h2Size, 28);
  const h3 = parsePositiveInt(g.h3Size, 24);
  if (tag === 'h1') return h1;
  if (tag === 'h2') return h2;
  if (tag === 'h3') return h3;
  const scales = { h4: 0.92, h5: 0.85, h6: 0.78 };
  const s = scales[tag] ?? 0.92;
  return Math.max(14, Math.round(h3 * s));
}

/**
 * @param {Record<string, unknown>|null|undefined} content
 * @returns {'h1'|'h2'|'h3'|'h4'|'h5'|'h6'}
 */
export function resolveSectionHeaderTitleTag(content) {
  const lvl = String(content?.sectionHeaderHeadingLevel || '').toLowerCase();
  if (LEVELS.includes(lvl)) return lvl;
  return 'h1';
}

/**
 * Tamanho em px do título do cabeçalho de seção, ou null para não definir inline.
 * @param {Record<string, unknown>|null|undefined} content
 * @param {Record<string, unknown>|null|undefined} globalConfig
 * @returns {number|null}
 */
export function resolveSectionHeaderTitleFontPx(content, globalConfig) {
  if (content?.sectionHeaderUseGlobalTypography) {
    const tag = resolveSectionHeaderTitleTag(content);
    return resolveGlobalHeadingPx(tag, globalConfig);
  }
  const explicit = parsePositiveInt(content?.sectionHeaderTitleFontPx, null);
  if (explicit != null) return explicit;
  const legacy = parsePositiveInt(content?.wrapperFont, null);
  if (legacy != null) return legacy;
  return null;
}

/**
 * Valor CSS de font-size do subtítulo (px ou rem).
 * @param {Record<string, unknown>|null|undefined} content
 * @param {Record<string, unknown>|null|undefined} globalConfig
 * @returns {string}
 */
export function resolveSectionHeaderSubtitleFontSizeCss(content, globalConfig) {
  if (content?.sectionHeaderUseGlobalTypography) {
    const p = parsePositiveInt(globalConfig?.pSize, null);
    if (p != null) return `${p}px`;
  }
  const sp = parsePositiveInt(content?.sectionHeaderSubtitleFontPx, null);
  if (sp != null) return `${sp}px`;
  return '1.1rem';
}

/**
 * Cor de fundo do cabeçalho de seção (aba Style → Cor de fundo), ou vazio se transparente.
 * @param {Record<string, unknown>|null|undefined} content
 * @returns {string}
 */
/** Faixa lateral esquerda do cabeçalho. Ausente no conteúdo antigo = habilitada. */
export function isSectionHeaderLeftBarEnabled(content) {
  return content?.headerLeftBarEnabled !== false;
}

export const SECTION_HEADER_MOBILE_STYLE_KEYS = [
  'sectionHeaderTagWeight',
  'sectionHeaderTagItalic',
  'sectionHeaderTitleWeight',
  'sectionHeaderTitleItalic',
  'sectionHeaderSubtitleWeight',
  'sectionHeaderSubtitleItalic',
  'sectionHeaderTitleSubtitleGap',
];

const HEADER_WEIGHTS = { light: 300, normal: 400, bold: 700 };

export function sectionHeaderWeightNumber(raw, fallback) {
  if (raw === 'light' || raw === 'normal' || raw === 'bold') return HEADER_WEIGHTS[raw];
  return fallback;
}

/** Peso, itálico e espaço entre título e subtítulo. Ausente = visual atual (tag/título bold, subtítulo normal, 8px). */
export function resolveSectionHeaderTypography(content) {
  const c = content || {};
  const gapRaw = parseInt(c.sectionHeaderTitleSubtitleGap, 10);
  const gap = Number.isFinite(gapRaw) ? Math.min(80, Math.max(0, gapRaw)) : 8;
  const part = (weightKey, italicKey, fallbackWeight) => ({
    fontWeight: sectionHeaderWeightNumber(c[weightKey], fallbackWeight),
    fontStyle: c[italicKey] ? 'italic' : 'normal',
  });
  return {
    tag: part('sectionHeaderTagWeight', 'sectionHeaderTagItalic', 700),
    title: part('sectionHeaderTitleWeight', 'sectionHeaderTitleItalic', 700),
    subtitle: part('sectionHeaderSubtitleWeight', 'sectionHeaderSubtitleItalic', 400),
    gap,
  };
}

/** Cores próprias. Título e subtítulo vazios herdam a cor do texto; tag vazia herda a faixa. */
export function resolveSectionHeaderColors(content) {
  const c = content || {};
  const text = String(c.textColor || '').trim();
  const bar = String(c.headerLeftBarColor || '').trim();
  const tag = String(c.sectionHeaderTagColor || '').trim();
  const title = String(c.sectionHeaderTitleColor || '').trim();
  const subtitle = String(c.sectionHeaderSubtitleColor || '').trim();
  return {
    tag: tag || bar,
    title: title || text,
    subtitle: subtitle || text,
    subtitleExplicit: subtitle !== '',
  };
}

export function buildSectionHeaderExportStyles(content, safeCssColorToken) {
  const paint = typeof safeCssColorToken === 'function' ? safeCssColorToken : (value, fallback) => value || fallback;
  const typo = resolveSectionHeaderTypography(content);
  const colors = resolveSectionHeaderColors(content);
  const textAlign = `text-align:${resolveSectionHeaderTextAlign(content)} !important;`;
  const weightCss = (n) => `font-weight:${n} !important;`;
  const italicCss = (style) => `font-style:${style};`;
  return {
    tagTypography: `${weightCss(typo.tag.fontWeight)}${italicCss(typo.tag.fontStyle)}`,
    titleTypography: `${weightCss(typo.title.fontWeight)}${italicCss(typo.title.fontStyle)}`,
    subtitleTypography: `${weightCss(typo.subtitle.fontWeight)}${italicCss(typo.subtitle.fontStyle)}margin-top:${typo.gap}px;`,
    titleColor: colors.title ? `color:${paint(colors.title, '#1e293b')} !important;` : 'color:inherit;',
    subColor: colors.subtitle ? `color:${paint(colors.subtitle, '#64748b')};` : 'color:inherit;',
    subOpacity: colors.subtitleExplicit ? '' : 'opacity:0.88;',
    tagColor: colors.tag ? `color:${paint(colors.tag, '#3b82f6')};` : '',
    textAlign,
  };
}

/** Alinhamento do bloco do cabeçalho. Ausente = esquerda. */
export function resolveSectionHeaderTextAlign(content) {
  const align = String(content?.textAlign || 'left').toLowerCase();
  if (align === 'center' || align === 'right') return align;
  return 'left';
}

export function resolveSectionHeaderBackground(content) {
  const c = content || {};
  const raw = String(c.wrapperBg || '').trim();
  if (!raw || raw === 'transparent') return '';
  return String(c.computedBgRGBA || raw).trim();
}
